import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function escapeHtml(value) {
  return (value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {{ items: HTMLElement; itemsMarkup: string; insertedHeaderTitle: HTMLElement | null } | null} */
  #optimisticEmptyState = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartAddPending, this.#handlePendingCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartAddPending, this.#handlePendingCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const openedFromAddToCart = event.detail?.data?.source === 'product-form-component' && event.detail?.data?.didError !== true;

    if (this.hasAttribute('auto-open') || openedFromAddToCart) {
      this.showDialog();
    }

    if (event.detail?.data?.didError) {
      this.#removeOptimisticItem(event.detail?.data?.optimisticToken);
    } else if (event.detail?.data?.sections) {
      this.#optimisticEmptyState = null;
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * @param {CustomEvent<{
   *   token: string,
   *   title: string,
   *   variantTitle: string,
   *   imageUrl: string,
   *   productUrl: string,
   *   priceText: string,
   *   quantity: number
   * }>} event
   */
  #handlePendingCartAdd = (event) => {
    const item = event.detail;
    if (!item?.token || !item.title) return;

    const tbody = this.#ensureOptimisticTableBody();
    if (!tbody) return;

    const existingRow = tbody.querySelector(`[data-optimistic-token="${CSS.escape(item.token)}"]`);
    if (existingRow) return;

    tbody.insertAdjacentHTML('afterbegin', this.#buildOptimisticRowMarkup(item));
    this.#updateStickyState();
  };

  /**
   * @returns {HTMLTableSectionElement | null}
   */
  #ensureOptimisticTableBody() {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return null;

    const existingTbody = dialog.querySelector('.cart-drawer__items .cart-items__table tbody');
    if (existingTbody instanceof HTMLTableSectionElement) {
      return existingTbody;
    }

    const itemsContainer = dialog.querySelector('.cart-drawer__items');
    if (!(itemsContainer instanceof HTMLElement)) return null;

    if (!this.#optimisticEmptyState) {
      const header = dialog.querySelector('.cart-drawer__header');
      let insertedHeaderTitle = null;

      if (header instanceof HTMLElement && !header.querySelector('#cart-drawer-heading')) {
        insertedHeaderTitle = document.createElement('h2');
        insertedHeaderTitle.id = 'cart-drawer-heading';
        insertedHeaderTitle.className = 'cart-drawer__heading h4';
        insertedHeaderTitle.textContent = Theme.translations.cart_count?.split(':')[0] || 'Cart';
        header.prepend(insertedHeaderTitle);
      }

      this.#optimisticEmptyState = {
        items: itemsContainer,
        itemsMarkup: itemsContainer.innerHTML,
        insertedHeaderTitle,
      };

      const emptyHeading = dialog.querySelector('.cart-drawer__heading--empty');
      if (emptyHeading instanceof HTMLElement) {
        emptyHeading.hidden = true;
      }
    }

    dialog.classList.remove('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');

    itemsContainer.innerHTML = `
      <div class="cart-items__wrapper cart-drawer__optimistic-shell">
        <div class="cart-items">
          <table class="cart-items__table" role="table">
            <tbody role="rowgroup"></tbody>
          </table>
        </div>
      </div>
    `;

    return itemsContainer.querySelector('tbody');
  }

  /**
   * @param {{
   *   token: string,
   *   title: string,
   *   variantTitle: string,
   *   imageUrl: string,
   *   productUrl: string,
   *   priceText: string,
   *   quantity: number
   * }} item
   * @returns {string}
   */
  #buildOptimisticRowMarkup(item) {
    const title = escapeHtml(item.title);
    const variantTitle = escapeHtml(item.variantTitle);
    const priceText = escapeHtml(item.priceText);
    const productUrl = escapeHtml(item.productUrl || '#');
    const imageUrl = escapeHtml(item.imageUrl);
    const quantity = Number.isFinite(item.quantity) && item.quantity > 0 ? item.quantity : 1;

    const imageMarkup = imageUrl
      ? `
          <a href="${productUrl}" class="cart-items__media-container" style="--ratio: 1;">
            <img class="cart-items__media-image border-style" src="${imageUrl}" alt="${title}" loading="eager">
          </a>
        `
      : `
          <div class="cart-items__media-container" style="--ratio: 1;"></div>
        `;

    const variantMarkup = variantTitle
      ? `
          <div class="cart-items__variants-wrapper">
            <dl class="cart-items__variants">
              <div class="cart-items__variant">
                <dd>${variantTitle}</dd>
              </div>
            </dl>
          </div>
        `
      : '';

    const priceMarkup = priceText
      ? `<span>${priceText}</span>`
      : `<span>${quantity} item${quantity > 1 ? 's' : ''}</span>`;

    return `
      <tr
        role="row"
        class="cart-items__table-row cart-items__table-row--full-width-variants"
        data-optimistic-token="${escapeHtml(item.token)}"
        style="opacity: 0.9;"
      >
        <td class="cart-items__media" role="cell">
          ${imageMarkup}
        </td>
        <td class="cart-items__details cart-primary-typography" role="cell">
          <div class="cart-items__product-info">
            <p>
              <a href="${productUrl}" class="cart-items__title">${title}</a>
            </p>
          </div>
          ${variantMarkup}
        </td>
        <td class="cart-items__quantity" role="cell">
          <div class="cart-items__quantity-controls">
            <div class="quantity-selector" aria-hidden="true">
              <button class="button quantity-minus button-unstyled" type="button" disabled>
                <span aria-hidden="true">&minus;</span>
              </button>
              <input type="number" value="${quantity}" disabled aria-label="Quantity">
              <button class="button quantity-plus button-unstyled" type="button" disabled>
                <span aria-hidden="true">+</span>
              </button>
            </div>
            <span class="cart-items__remove">
              <span class="cart-items__remove-label">Adding...</span>
            </span>
          </div>
        </td>
        <td class="cart-items__error hidden" role="cell"></td>
        <td class="cart-items__price cart-secondary-typography" role="cell">
          ${priceMarkup}
        </td>
      </tr>
    `;
  }

  /**
   * @param {string | undefined} optimisticToken
   */
  #removeOptimisticItem(optimisticToken) {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (optimisticToken) {
      dialog.querySelector(`[data-optimistic-token="${CSS.escape(optimisticToken)}"]`)?.remove();
    } else {
      dialog.querySelectorAll('[data-optimistic-token]').forEach((row) => row.remove());
    }

    const hasOptimisticRows = dialog.querySelector('[data-optimistic-token]') != null;
    if (hasOptimisticRows || !this.#optimisticEmptyState) return;

    this.#optimisticEmptyState.items.innerHTML = this.#optimisticEmptyState.itemsMarkup;
    this.#optimisticEmptyState.insertedHeaderTitle?.remove();
    dialog.classList.add('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');

    const emptyHeading = dialog.querySelector('.cart-drawer__heading--empty');
    if (emptyHeading instanceof HTMLElement) {
      emptyHeading.hidden = false;
    }

    this.#optimisticEmptyState = null;
    this.#updateStickyState();
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
